import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import differenceInDays from 'date-fns/difference_in_days';
import differenceInHours from 'date-fns/difference_in_hours';
import { FormattedMessage, injectIntl } from 'react-intl';
import classNames from 'classnames';

import 'Components/Timers/CountdownToLaunch.scss';

const calculateCountdown = (countDownEndDate) => {
  const timeLeft = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (countDownEndDate > Date.now()) {
    const dealTimeLeft = (countDownEndDate - Date.now()) / 1000;
    const remainingMins = dealTimeLeft / 60;

    timeLeft.days = Math.floor(remainingMins / 1440);
    timeLeft.hours = Math.floor((remainingMins / 60) % 24);
    timeLeft.minutes = Math.floor(remainingMins % 60);
    timeLeft.seconds = Math.floor(dealTimeLeft % 60);
  }

  return timeLeft;
};

function CountdownToLaunch({ releaseDate, playableFrom, theme, earlyAccess }) {
  const dateToUse = earlyAccess ? playableFrom : releaseDate;
  const [time, setTime] = useState(calculateCountdown(dateToUse));

  useEffect(() => {
    let timer;
    if (dateToUse > Date.now()) {
      timer = setTimeout(() => {
        let timeLeft = {
          days: 0,
          hours: 0,
          minutes: 0,
          seconds: 0,
        };

        timeLeft = calculateCountdown(dateToUse);
        setTime(timeLeft);
      }, 1000);
    }
    // Clear timeout if the component is unmounted
    return () => clearTimeout(timer);
  });

  const { days, hours, minutes, seconds } = time;

  const earlyAccessDiffDays = differenceInDays(
    new Date(releaseDate), new Date(playableFrom),
  );

  const earlyAccessDiffHours = differenceInHours(
    new Date(releaseDate), new Date(playableFrom),
  );

  const daysLeft = differenceInDays(new Date(dateToUse), new Date());
  if (new Date().toISOString() > dateToUse) return false;

  const showHoursAndMinutes = daysLeft < 30;
  let showSeconds = false;
  if (days === 0 && hours === 0 && minutes === 0) showSeconds = true;

  return (
    <div className={classNames('launch-timer', { [`launch-timer--${theme}`]: theme !== '' })}>
      <div className="content-container">
        <div className="countdown-to-launch">
          {!earlyAccess &&
            <FormattedMessage
              id="countdown-to-launch.timer.text"
              defaultMessage="Countdown to release:"
            />
          }
          {earlyAccess && earlyAccessDiffHours <= 72 &&
            <FormattedMessage
              id="countdown-to-launch.timer.text.early-access"
              defaultMessage="Buy now and get {time} hours early access"
              values={{
                time: earlyAccessDiffHours,
              }}
            />
          }
          {earlyAccess && earlyAccessDiffHours > 72 &&
            <FormattedMessage
              id="countdown-to-launch.timer.text.early-access-days"
              defaultMessage="Buy now and get {time} days early access"
              values={{
                time: earlyAccessDiffDays,
              }}
            />
          }
          &nbsp;
        </div>

        {!showHoursAndMinutes &&
          <div className="day-countdown">
            {daysLeft}
            <span className="day-text"><FormattedMessage id="countdown-to-launch.just-days" defaultMessage="Days" /></span>
          </div>
        }

        {(showHoursAndMinutes && (days > 0 || hours > 0 || minutes > 0 || seconds > 0)) &&
          <span className="timer-numbers">
            <div className="number-container">
              {days > 0 &&
                <div className="number">
                  {days}
                  <div className="number-text">
                    <FormattedMessage
                      id="countdown-to-launch.launch-timer.days"
                      defaultMessage="{count, plural, one {Day} other {Days}}"
                      tagName="span"
                      values={{
                        count: days,
                      }}
                    />
                    <React.Fragment>,</React.Fragment>
                  </div>
                </div>
              }

              {hours > 0 &&
                <div className="number">
                  {hours}
                  <div className="number-text">
                    <FormattedMessage
                      id="countdown-to-launch.launch-timer.hours"
                      defaultMessage="{count, plural, one {Hour} other {Hours}}"
                      tagName="span"
                      values={{
                        count: hours,
                      }}
                    />
                    <React.Fragment>,</React.Fragment>
                  </div>
                </div>
              }

              <div className="number">
                {minutes}
                <div className="number-text">
                  <FormattedMessage
                    id="countdown-to-launch.launch-timer.minutes"
                    defaultMessage="{count, plural, one {Minute} other {Minutes}}"
                    tagName="span"
                    values={{
                      count: minutes,
                    }}
                  />
                  {showSeconds &&
                    <React.Fragment>,</React.Fragment>
                  }
                </div>
              </div>

              {showSeconds &&
                <div className="number">
                  {seconds}
                  <div className="number-text">
                    <FormattedMessage
                      id="countdown-to-launch.launch-timer.seconds"
                      defaultMessage="{count, plural, one {Second} other {Seconds}}"
                      tagName="span"
                      values={{
                        count: seconds,
                      }}
                    />
                  </div>
                </div>
              }
            </div>
          </span>
        }
      </div>
    </div>
  );
}

CountdownToLaunch.propTypes = {
  releaseDate: PropTypes.number,
  playableFrom: PropTypes.number,
  theme: PropTypes.string,
  earlyAccess: PropTypes.bool,
};

CountdownToLaunch.defaultProps = {
  releaseDate: 0,
  playableFrom: 0,
  theme: '',
  earlyAccess: false,
};

export default injectIntl(CountdownToLaunch);
